import { ReactNode, useCallback, useEffect, useRef } from "react";
import Map, { MapRef, ViewState } from "react-map-gl";
import "./mapboxIcons.css";
import "mapbox-gl/dist/mapbox-gl.css";
import GeoRoute from "./GeoRoute";
import { initialMapStyle, useLayerVisibilityState, useMapStyle } from "../../features/mapDisplay/mapDisplaySlice";
import SelectionWaypoints from "./SelectionWaypoints";
import {
    RiskViewerKind,
    RouteViewState,
    useRiskViewerState,
    useRiskViewerStateKind,
    useRouteViewState
} from "../../features/riskViewer/riskViewerSlice";
import GeoViewOverlay from "./GeoViewOverlay";
import { AllForecastComponents } from "../../app/forecastSurface";
import { useIsMobile } from "../../features/pageWidth/pageWidthSlice";
import { useDispatch } from "react-redux";
import { DrawerStateKinds, useDrawerState, useSelectedWaypointInputIndex } from "../../features/Drawer/drawerSlice";
import { selectWaypointFromMap } from "../location/LocationSelectModal";
import ImpactDataLayer from "./ImpactDataLayer";
import WeatherAlertsLayer from "../WeatherAlerts/WeatherAlertsLayer";
import { useShouldShowAlerts } from "../../features/alertDisplay/alertDisplaySlice";
import { useHasOngoingFetches } from "../../features/time/playbackSlice";
import { ROUTE_DISPLAY_TYPES } from "./geoUtils";
import WindStreamlinesLayer from "../WindStreamlines/WindStreamlinesLayer";

export const initialViewState = {
    longitude: -100.3,
    latitude: 38,
    zoom: 4,
} as ViewState;


type GeoViewProps = {
    dataFetchFailed: boolean,
    children: ReactNode,
    refetchRiskData: ( tauTimeout: boolean ) => void,
    components: AllForecastComponents | undefined,
};
export const GeoView = ( {
    children,
    refetchRiskData,
    components
}: GeoViewProps ) =>
{
    const mapRef = useRef<MapRef>( null );
    const riskViewerState = useRiskViewerState( );
    const routesToDisplay = riskViewerState.routesToDisplay;
    const routeId = riskViewerState.selectedRoute;
    const homeViewState = riskViewerState.homeViewState;
    const isMobile = useIsMobile();

    const riskViewerStateKind = useRiskViewerStateKind();
    const selectedWaypointInputIndex = useSelectedWaypointInputIndex();
    const drawerState = useDrawerState();
    const showAlerts = useShouldShowAlerts();
    const method = ( e: { lngLat: { lat: number, lng: number } } ) =>
    {
        if ( ( drawerState.kind === DrawerStateKinds.TripInput || drawerState.kind === DrawerStateKinds.Edit ) && riskViewerStateKind === RiskViewerKind.SELECTION_STATE )
        {
            selectWaypointFromMap( e.lngLat, dispatch, selectedWaypointInputIndex );
        }
    };
    const onMapLoad = useCallback( () =>
    {
        const map = mapRef?.current?.getMap();
        //turn off option to rotate map with SHIFT+Cursor
        map?.keyboard.disableRotation();
        map?.touchZoomRotate.disableRotation();
    }, [ ] );
    const isLayerEnabledTracker = useLayerVisibilityState();
    const mapStyleState = useMapStyle();
    const dispatch = useDispatch();
    const routeViewState = useRouteViewState();
    const hasOngoingFetches = useHasOngoingFetches();

    const routeElements = routesToDisplay?.map( ( route, index ) => (
        <GeoRoute
            key = {`Route-To-Display-${index}`}
            mapRef = {mapRef}
            propRouteId = {route?.routeId}
            routeNumber = {index}
            isSelected = {routeId?.hexString === route?.routeId?.hexString}
        />
    ) );

    useEffect( () =>
    {
        const map = mapRef?.current?.getMap();
        if ( !map ) return;

        const handleLayerUpdate = () =>
        {
            const matchedIndex = routesToDisplay?.findIndex(
                route => route?.routeId?.hexString === routeId?.hexString
            );
            if ( matchedIndex === undefined || matchedIndex < 0 ) return;

            const layerTypes = Object.values( ROUTE_DISPLAY_TYPES );
            const allLayers = map.getStyle().layers ?? [];

            const routeLayerRegexes = layerTypes.map( type =>
            {
                const prefix = `route-layer-${type ? `${type}-` : ""}${matchedIndex}-`;
                return new RegExp( `^${prefix}\\d+$` );
            } );

            const bottomLayers = [
                "route-layer-background-outer",
                "route-layer-background-inner"
            ];

            bottomLayers.forEach( layerId =>
            {
                if ( map.getLayer( layerId ) !== undefined )
                {
                    map.moveLayer( layerId );
                }
            } );

            allLayers.forEach( layer =>
            {
                const layerId = layer.id;
                if ( routeLayerRegexes.some( regex => regex.test( layerId ) ) )
                {
                    try
                    {
                        map.moveLayer( layerId );
                    }
                    catch ( e )
                    {
                        console.warn( `Failed to move layer ${layerId}:`, e );
                    }
                }
            } );

            if ( map.getLayer( "open-roads-layer" ) !== undefined )
            {
                map.moveLayer( "open-roads-layer" );
            }

        };

        // Initial run
        handleLayerUpdate();

        // Attach listener
        map.on( "sourcedata", handleLayerUpdate );

        // Cleanup
        return () =>
        {
            map.off( "sourcedata", handleLayerUpdate );
        };
    }, [ routeId, mapRef, routesToDisplay, hasOngoingFetches ] );


    useEffect( () =>
    {
        const map = mapRef.current?.getMap();
        if ( map && homeViewState !== undefined )
        {
            const [ [ west, south ], [ east, north ] ] = homeViewState;

            // Calculate a dynamic expand percentage based on screenwidth
            const screenWidth = window.innerWidth;
            const expandPercentage = 400 / screenWidth;
            // Expand the width based on the calculated percentage
            const width = east - west;
            const expandedWidth = width * ( 1 + expandPercentage );
            const newWest = west - ( expandedWidth - width );
            const newEast = east;

            map.fitBounds( isMobile
                ? [ [ west, south ], [ east, north ] ]
                : [ [ newWest, south ], [ newEast, north ] ],
            { duration: 0 } ); // duration in milliseconds
        }
        else if ( map && homeViewState === undefined )
        {
            map.fitBounds( [ [ -155, 22.396 ], [ -60.8, 50.384 ] ], { duration: 0 } );
        }

    }, [ homeViewState ] );

    useEffect( () =>
    {
        const map = mapRef.current?.getMap();
        if ( map && mapStyleState !== undefined )
        {
            map.setStyle( mapStyleState );
        }

    }, [ mapStyleState ] );

    return (
        <Map
            id = "myMap"
            ref = {mapRef}
            initialViewState = {initialViewState}
            reuseMaps
            mapStyle = {initialMapStyle}
            dragRotate = {false}
            onLoad = {onMapLoad}
            onClick = {method}
        >
            {children}
            <GeoViewOverlay
                allComponents = {components}
                mapRef = {mapRef}
            />
            {routeViewState !== RouteViewState.COLORED_ROUTE && !showAlerts && <ImpactDataLayer refetchRiskData = {refetchRiskData}/>}
            {showAlerts && <WeatherAlertsLayer/>}
            <WindStreamlinesLayer/>
            {/*By treating each route as its own layer, we have hardcoded their order in the layers. The alternative is to modify the layers every time the selected route changes. */}
            {isLayerEnabledTracker.Route && ( routesToDisplay ? routeElements : <GeoRoute propRouteId = {undefined} mapRef = {mapRef} routeNumber = {0} isSelected/> )}
            <SelectionWaypoints/>
        </Map>
    );
};
